import { useDocumentsRepository } from '@Hooks/useRepository';
import { type ApiTemplateCustomization, decode, decodeAll, encode, encodeAll } from '@Model';
import { CustomizationsRepository } from '@Repositories/Documents';
import { isNullOrEmpty, unwrapOr, useToasts } from '@synqo/frontend-component-library';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

export interface ICustomizationOptions {
    customizations: ApiTemplateCustomization[];
    isError: boolean;
    isLoading: boolean;
    saveCustomizations: (customizations: ApiTemplateCustomization[]) => Promise<ApiTemplateCustomization[]>;
    updateCustomization: (item: ApiTemplateCustomization) => Promise<ApiTemplateCustomization>;
    refetchCustomizations: () => Promise<ApiTemplateCustomization[] | undefined>;
}

export function useCustomizationOptions(printOptionId: string | undefined): ICustomizationOptions {
    const customizationsApi = useDocumentsRepository(CustomizationsRepository);

    const { pushAllToasts } = useToasts();

    const {
        data: customizations,
        refetch,
        isError,
        isLoading,
    } = useQuery({
        queryFn: async () => {
            const result = await customizationsApi.getTemplateCustomization(printOptionId ?? '');
            const customizations = unwrapOr(result, []);
            return decodeAll(customizations);
        },
        queryKey: ['documents', 'print_options', printOptionId, 'customizations'],
        enabled: !isNullOrEmpty(printOptionId),
        initialData: [],
    });

    const { mutateAsync: saveCustomizations } = useMutation({
        mutationFn: async (customizations: ApiTemplateCustomization[]) => {
            const encoded = encodeAll(customizations);
            const result = await customizationsApi.saveTemplateCustomization(printOptionId ?? '', encoded);

            pushAllToasts(result.messages);
            const updated = unwrapOr(result, []);
            return decodeAll(updated);
        },
    });

    const { mutateAsync: updateCustomization } = useMutation({
        mutationFn: async (customization: ApiTemplateCustomization) => {
            const encoded = encode(customization);
            const result = await customizationsApi.updateTemplateCustomization(printOptionId ?? '', encoded);

            pushAllToasts(result.messages);
            const updated = unwrapOr(result, customization);
            return decode(updated);
        },
    });

    const refetchCustomizations = useCallback(async () => {
        const { data } = await refetch();
        return data;
    }, [refetch]);

    return {
        customizations,
        saveCustomizations,
        updateCustomization,
        refetchCustomizations,
        isError,
        isLoading,
    };
}
